import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Row, Col, Collapse, Button, Container, Alert } from 'reactstrap';
import classNames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import { compose } from 'redux';

import { formatDate, getFileName, mapDataToSerie } from '../utils/index';
import { setSelected as setSelectedAction, setUnselected } from '../actions/experimentsList';
import Chart from '../components/chart';
import ExperimentAnnotationsModal from './experimentAnnotationsModal';
import ValuesTable from '../components/valuesTable';
import { idsTypeIds } from '../constants';
import * as notify from '../../main/utils/notify';
import { withSendRequest } from '../../main/hoc/withSendRequest';


const ExperimentItem = ({ experiment, index, sendRequest }) => {
  const { id, pulse, run, machine, variables, date } = experiment;

  const [ upperOpen, setUpperOpen ] = useState(false);
  const [ lowerOpen, setLowerOpen ] = useState(false);
  const [ selected, setSelected ] = useState(false);
  const [ annotationsModalOpen, setAnnotationsModal ] = useState(false);
  const [ annotationsCount, setAnnotationsCount ] = useState(0);
  const [ chartsCount, setChartsCount ] = useState(0);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const switchOpen = useSelector((state) => state.experiments.get('switchOpen'));
  const currentFilters = useSelector((state) => state.experiments.get('currentFilters'));
  const selectedExperiments = useSelector((state) => state.experiments.get('selectedExperiments'));

  const cardHeaderClass = classNames({
    'zebra-odd': index % 2 === 1,
    'zebra-even': index % 2 === 0
  });

  const upperCollapseBtnClass = classNames('btn-bg-image', {
    'btn-collapse-open': upperOpen,
    'btn-collapse-closed': !upperOpen
  });

  const lowerCollapseBtnClass = classNames('btn-bg-image btn-toggle-lower', {
    'btn-collapse-open': lowerOpen,
    'btn-collapse-closed': !lowerOpen
  });

  useEffect(() => {
    if (!switchOpen && currentFilters.size === 0) {
      setUpperOpen(false);
    }
    else if (!switchOpen && currentFilters.size !== 0) {
      setUpperOpen(false);
    }
    else if (switchOpen && currentFilters.size !== 0) {
      setUpperOpen(true);
    }
  }, [ switchOpen, currentFilters ]);
  
  useEffect(() => {
    if (selectedExperiments.includes(id) && !selected) {
      setSelected(true);
    }
    else if (!selectedExperiments.includes(id) && selected) {
      setSelected(false);
    }
  },[ selectedExperiments, selected, id ]);

  useEffect(() => {
    if (variables && Object.keys(variables).length !== 0) {
      setChartsCount(variables.filter((data) => data.is_time_dependent).length);
    }
  }, [ variables ]);

  useEffect(() => {
    const fetchAnnotations = async () => {
      try {
        const response = await sendRequest('get', `experiments/${id}/annotations`);
        setAnnotationsCount(response.data.length);
      }
      catch (err) {
        notify.error(t('annotations_modal.fetching_data_error'));
      }
    };
    fetchAnnotations();
  }, [ annotationsModalOpen, sendRequest, id, t ]);

  const toggleUpperOpen = () => setUpperOpen((prev) => !prev);

  const toggleLowerOpen = () => setLowerOpen((prev) => !prev);

  const handleCheckboxChange = () => {
    setSelected((prev) => !prev);

    if (!selected) {
      dispatch(setSelectedAction(id));
    }
    else {
      dispatch(setUnselected(id));
    }
  };

  const toggleAnnotationsModal = () => setAnnotationsModal((prev) => !prev);

  const showLowerCollapseButton = () => chartsCount > currentFilters.size; 
  
  const renderChartsWithFilters = () => (
    <>
      <div className='filtered-charts w-100'>
        <div className='d-flex flex-column align-items-center'>
          {variables.slice(0, variables.length)
            .filter(({ id }) => currentFilters.includes(""+id))
            .map((data) =>
              data.is_time_dependent ? ( data.idsTypeId !== idsTypeIds.pedestal ?
                <Chart
                  key={data.ids_field}
                  data={mapDataToSerie(data.data?.[0])}
                  title={data.name || data.ids_field}
                  description={data.definition}
                  unit={data.unit || ' '}
                  boldLabel
                  fileName={getFileName(data.ids_field, pulse, run, machine)}
                /> :
                <Col className={"pedestal-entry"}>
                  <span className={"font-weight-bold"}>{data.ids_field}</span>
                  <ValuesTable groupedEntryData={mapDataToSerie(data.data?.[0])} />
                </Col>
              ) : (null)
            )
          }
        </div>
        {showLowerCollapseButton() ? 
          <Button 
            color='link' 
            className={lowerCollapseBtnClass}
            onClick={toggleLowerOpen}
            title={t('expand_collapse_section_label')}
          >
          </Button>
          : null}
      </div>
      <Collapse isOpen={lowerOpen}>
        <div className='d-flex flex-column align-items-center'>
          {variables.slice(0, variables.length)
            .filter(({ id }) => !currentFilters.includes(""+id))
            .map((data) =>
              data.is_time_dependent ? ( data.idsTypeId !== idsTypeIds.pedestal ?
                <Chart
                  key={data.ids_field}
                  data={mapDataToSerie(data.data?.[0])}
                  title={data.name || data.ids_field}
                  description={data.definition}
                  unit={data.unit || ' '}
                  fileName={getFileName(data.ids_field, pulse, run, machine)}
                /> :
                <Col className={"pedestal-entry"}>
                  <span>{data.ids_field}</span>
                  <ValuesTable groupedEntryData={mapDataToSerie(data.data?.[0])} />
                </Col>
              ) : (null)
            )
          }
        </div>
      </Collapse>
    </>
  );

  const renderChartsWithoutFilters = () => (
    <div className='d-flex flex-column align-items-center'>
      {variables.slice(0, variables.length).map((data) =>
        data.is_time_dependent ? ( data.idsTypeId !== idsTypeIds.pedestal ?
          <Chart
            key={data.ids_field}
            data={mapDataToSerie(data.data?.[0])}
            title={data.name || data.ids_field}
            description={data.definition}
            unit={data.unit || ' '}
            fileName={getFileName(data.ids_field, pulse, run, machine)}
          /> :
          <Col className={"pedestal-entry"}>
            <span>{data.ids_field}</span>
            <ValuesTable groupedEntryData={mapDataToSerie(data.data?.[0])} />
          </Col>
        ) : (null)
      )}
    </div>
  );

  if (!variables || Object.keys(variables).length === 0) {
    return (
      <Alert color='danger' className='entry-data-not-found-alert'>
        <Trans i18nKey='entry_data_error_message'>
          Entry data for experiment with id <strong>{{ id: id }}</strong> not found
        </Trans>
      </Alert>
    );
  };

  const wrongImport = (pulse===null || run===null || machine===null);

  return (
    <>
      { annotationsModalOpen && <ExperimentAnnotationsModal
        experimentId={experiment.id}
        isOpen={annotationsModalOpen}
        toggle={toggleAnnotationsModal}
      /> }
      <Card>
        <CardHeader className={cardHeaderClass + (wrongImport ? " wrong-import":"")}>
          <Container>
            <Row className='align-items-center' title={wrongImport ? t('unknown_label'):null}>
              <Col md='1'>
                <input type='checkbox' checked={selected} onChange={handleCheckboxChange} title={t('select_experiment_checkbox_tooltip')}/>
              </Col>
              <Col>
                <span>{t('shot_label')}:{' '}{pulse ?? t('unknown')}</span>
              </Col>
              <Col>
                <span>{t('run_label')}:{' '}{run ?? t('unknown')}</span>
              </Col>
              <Col>
                <span>{t('machine_label')}:{' '}{machine ?? t('unknown')}</span>
              </Col>
              <Col>
                <span>{t('date_label')}:{' '}{formatDate(date)}</span>
              </Col>
              <Col>
                <div className='d-flex justify-content-between align-items-center'>
                  <div className='d-flex align-items-center'>
                    <Link 
                      to={`/experiments/${id}`}
                      className='open-shot-link btn-bg-image btn-open mr-2' 
                      title={t('open_shot_details_label')}
                    >
                    </Link>
                    <Button 
                      onClick={toggleAnnotationsModal}
                      color='link'
                      className={ 'btn-bg-image btn-annotate' + (annotationsCount ? '-green' : '') }
                      title={t('open_shot_annotations_label') + (annotationsCount ? ` (${annotationsCount})` : '') }
                    >
                    </Button>
                  </div>
                  <span>
                    <Button 
                      color='link' 
                      className={upperCollapseBtnClass}
                      title={t('expand_collapse_section_label')}
                      onClick={toggleUpperOpen}
                    >
                    </Button>
                  </span>
                </div>
              </Col>
            </Row>
          </Container>
        </CardHeader>
        <Collapse isOpen={upperOpen}>
          <CardBody>
            {currentFilters.size === 0 ? renderChartsWithoutFilters() : renderChartsWithFilters()}
          </CardBody>
        </Collapse>
      </Card>
    </>
  );
};

ExperimentItem.propTypes = {
  experiment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    pulse: PropTypes.number.isRequired,
    run: PropTypes.number.isRequired,
    machine: PropTypes.string.isRequired,
    uri: PropTypes.string,
    user: PropTypes.string,
    version: PropTypes.string,
    date: PropTypes.string,
    variables: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      ids_field: PropTypes.string,
      is_time_dependent: PropTypes.bool,
      type: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any,
        time: PropTypes.any,
      }))
    })),
  }),

  index: PropTypes.number.isRequired,
  sendRequest: PropTypes.func, // HOC
};

export default compose(
  withSendRequest,
)(ExperimentItem);

